<template>
  <div>
    <v-toolbar dense tabs color="primary darken-2" dark class="page_title">
      <v-toolbar-title class="pa-0 pr-0 mt-1">
        <v-layout row>
          <v-flex xs10 sm11 text-xs-left>
            <a href="javascript: history.go(-1)" class="pa-0">
              <v-btn flat fab small class="pa-0">
                <i class="fas fa-arrow-left headline"></i>
              </v-btn>
            </a>
            Referred Users
          </v-flex>
          <v-flex xs2 sm1 text-xs-right>
            <router-link :to="$store.state.close_url2" class="pa-0 text-xs-right">
              <v-btn flat fab small class="pa-0">
                <i class="fas fa-close headline"></i>
              </v-btn>
            </router-link>
          </v-flex>
        </v-layout>
      </v-toolbar-title>
    </v-toolbar>

    <div class="dashboard">
      <!-- Total Commissions -->
      <div class="total-commission-card">
        <div class="total-content">
          <span class="total-label">Total Commissions:</span>
          <span class="total-amount">{{ totalCommissions.toLocaleString('en-US') }} FCFA</span>
        </div>
      </div>

      <!-- Filters -->
      <div class="filter-card">
        <div class="filter-group">
          <div class="date-filters">
            <div class="date-filter-group">
              <label>Start Date</label>
              <input 
                type="date" 
                v-model="startDate" 
                class="date-input"
                @change="fetchAffiliatedUsers"
              >
            </div>
            <div class="date-filter-group">
              <label>End Date</label>
              <input 
                type="date" 
                v-model="endDate" 
                class="date-input"
                @change="fetchAffiliatedUsers"
              >
            </div>
          </div>

          <div class="filter-controls">
            <select 
              v-model="selectedStatus" 
              class="status-select"
              @change="fetchAffiliatedUsers"
            >
              <option v-for="option in statusOptions" :value="option" :key="option">
                {{ option }}
              </option>
            </select>

            <button 
              class="refresh-button"
              @click="fetchAffiliatedUsers"
              :disabled="loading"
            >
              <span class="button-content">
                {{ loading ? 'Loading...' : 'Refresh' }}
              </span>
            </button>
          </div>
        </div>

        <div class="search-box">
          <input 
            type="text" 
            v-model="search" 
            placeholder="Search..."
            class="search-input"
          >
        </div>
      </div>

      <!-- Table -->
      <div class="affiliates-table">
        <div class="table-header">
          <h3>Affiliated Users ({{ affiliatedUsers.length }})</h3>
        </div>

        <table>
          <thead>
            <tr>
              <th 
                v-for="header in headers" 
                :key="header.value"
                @click="sortTable(header.value)"
                :class="{ 'active-sort': sortBy === header.value }"
              >
                {{ header.text }}
                <span class="sort-indicator">
                  {{ sortDesc ? '▼' : '▲' }}
                </span>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr 
              v-for="user in filteredUsers" 
              :key="user.id"
              class="table-row"
            >
              <td>{{ user.username }}</td>
              <td>
                <div class="date-cell">
                  <span class="calendar-icon">📅</span>
                  {{ formatDate(user.created_at) }}
                </div>
              </td>
              <td>
                <span class="commission-badge">
                  {{ user.total_commission.toLocaleString('en-US') }}
                </span>
              </td>
              <td>
                <span 
                  class="status-badge" 
                  :class="{
                    'paid': user.payment_status,
                    'pending': !user.payment_status
                  }"
                >
                  {{ user.payment_status ? 'Paid' : 'Pending' }}
                </span>
              </td>
              <td>{{ user.payment_method || '-' }}</td>
            </tr>
            <tr v-if="!filteredUsers.length">
              <td colspan="5" class="no-results">No results found</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  data() {
    return {
      loading: false,
      search: '',
      sortBy: 'created_at',
      sortDesc: true,
      startDate: null,
      endDate: null,
      selectedStatus: 'All',
      statusOptions: ['All', 'Paid', 'Pending'],
      totalCommissions: 0,
      headers: [
        { text: 'User', value: 'username' },
        { text: 'Affiliation Date', value: 'created_at' },
        { text: 'Total Commission', value: 'total_commission' },
        { text: 'Payment Status', value: 'payment_status' },
        { text: 'Payment Method', value: 'payment_method' }
      ],
      affiliatedUsers: []
    }
  },

  computed: {
    filteredUsers() {
      return this.affiliatedUsers.filter(user => {
        const matchesSearch = Object.values(user).some(value => 
          String(value).toLowerCase().includes(this.search.toLowerCase())
        )
        const dateInRange = this.checkDateRange(user.created_at)
        const statusMatches = this.checkStatus(user.payment_status)
        return matchesSearch && dateInRange && statusMatches
      })
    }
  },

  mounted() {
    this.fetchAffiliatedUsers()
  },

  methods: {
    async fetchAffiliatedUsers() {
      this.loading = true
      try {
        const params = {
          sort_by: this.sortBy,
          sort_order: this.sortDesc ? 'desc' : 'asc',
          start_date: this.startDate,
          end_date: this.endDate,
          status: this.selectedStatus
        }

        const response = await axios.get('/api/v1/home/affiliated-users', { params })
        this.affiliatedUsers = response.data.users
        this.totalCommissions = response.data.total_commissions || 0
      } catch (error) {
        console.error('Error:', error)
        this.showError('Error loading data')
      } finally {
        this.loading = false
      }
    },

    formatDate(dateString) {
      const options = { year: 'numeric', month: 'short', day: 'numeric' }
      return new Date(dateString).toLocaleDateString('en-US', options)
    },

    sortTable(column) {
      if (this.sortBy === column) {
        this.sortDesc = !this.sortDesc
      } else {
        this.sortBy = column
        this.sortDesc = true
      }
      this.fetchAffiliatedUsers()
    },

    checkDateRange(date) {
      if (!this.startDate && !this.endDate) return true
      const userDate = new Date(date)
      const start = this.startDate ? new Date(this.startDate) : null
      const end = this.endDate ? new Date(this.endDate) : null
      return (!start || userDate >= start) && (!end || userDate <= end)
    },

    checkStatus(status) {
      if (this.selectedStatus === 'All') return true
      return this.selectedStatus === 'Paid' ? status : !status
    },

    showError(message) {
      alert(message)
    }
  }
}
</script>

<style scoped>
.dashboard {
  max-width: 1200px;
  margin: 2rem auto;
  padding: 0 1rem;
}

.total-commission-card {
  background: #4a90e2;
  color: white;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.total-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.total-label {
  font-size: 1.1rem;
}

.total-amount {
  font-size: 1.5rem;
  font-weight: bold;
}

.filter-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.filter-group {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.date-filters {
  display: flex;
  gap: 1rem;
  flex-grow: 1;
  flex-wrap: wrap;
}

.date-filter-group {
  flex: 1;
  min-width: 200px;
}

.date-filter-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #666;
  font-size: 0.9rem;
}

.date-input, .status-select {
  width: 100%;
  padding: 0.5rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9rem;
}

.filter-controls {
  display: flex;
  gap: 1rem;
  flex-grow: 1;
  min-width: 300px;
}

.status-select {
  flex: 1;
}

.refresh-button {
  background: #4a90e2;
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.3s ease;
  min-width: 120px;
}

.refresh-button:hover {
  background: #357abd;
}

.search-box {
  margin-top: 1rem;
}

.search-input {
  width: 100%;
  padding: 0.5rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.affiliates-table {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
}

th, td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
}

th {
  background: #f8f9fa;
  cursor: pointer;
  user-select: none;
}

.active-sort {
  color: #4a90e2;
  font-weight: 600;
}

.sort-indicator {
  margin-left: 0.5rem;
  font-size: 0.8em;
}

.table-row:hover {
  background-color: #f8f9fa;
}

.date-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.commission-badge {
  background: #4a90e2;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  display: inline-block;
}

.status-badge.paid {
  background: #34c759;
}

.status-badge.pending {
  background: #ff9500;
}

.no-results {
  text-align: center;
  padding: 2rem;
  color: #666;
}

@media (max-width: 768px) {
  .filter-group {
    flex-direction: column;
  }
  
  .date-filters {
    flex-direction: column;
  }

  .filter-controls {
    flex-direction: column;
  }

  .date-filter-group {
    min-width: unset;
  }

  th, td {
    padding: 0.75rem;
    font-size: 0.9rem;
  }

  .refresh-button {
    width: 100%;
  }

  .status-select {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .total-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .date-cell {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .calendar-icon {
    display: none;
  }
}
.dashboard {
  max-width: 1400px; /* Augmentation de la largeur maximale */
  margin: 3rem auto; /* Plus d'espace en haut */
  padding: 0 2rem; /* Espacement sur les côtés */
}

/* Ajout d'espace autour du tableau */
.affiliates-table {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  margin: 2rem 0; /* Espacement vertical ajouté */
  padding: 1.5rem; /* Rembourrage interne augmenté */
}

/* Ajustements pour les cartes */
.total-commission-card {
  margin-top: 1.5rem; /* Espacement ajouté en haut */
  margin-bottom: 2.5rem; /* Espacement augmenté en bas */
}

.filter-card {
  margin-bottom: 2.5rem; /* Espacement augmenté en bas */
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .dashboard {
    padding: 0 1rem; /* Maintien de l'espacement sur mobile */
    margin: 2rem auto; /* Ajustement pour les petits écrans */
  }
  
  .affiliates-table {
    padding: 1rem; /* Rembourrage réduit sur mobile */
    margin: 1.5rem 0; /* Espacement réduit sur mobile */
  }
}

@media (max-width: 480px) {
  .dashboard {
    padding: 0 0.5rem; /* Espacement minimal sur très petits écrans */
  }
  
  .total-commission-card,
  .filter-card {
    margin: 1rem 0; /* Espacement uniforme sur mobile */
  }
}
</style>